import { useContext, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { consumeConfirmResume } from 'utils/shopifyConfirmResume'

import useProductDetails from 'containers/products/useProductDetails'

import ScreenLoading from 'components/ScreenLoading'
import Switch from 'components/Switch'

import ProductCard from './ProductCard'
import ShopifyPromoCode from './ShopifyPromoCode'
import './Products.scss'
import OrganizationsContext from 'containers/organizations/organizationsContext'
import { IShopifyPromoCode, Product, ProductMode } from 'types'

const Products = () => {
  const { currentOrganization, isLoadingOrganization } = useContext(OrganizationsContext)
  const { products, getProducts, loadingProducts, productError } =
    useProductDetails(currentOrganization)
  const [annualToggle, setAnnualToggle] = useState(true)
  const [subscriptionProducts, setSubscriptionProducts] = useState<Product[]>([])
  const [paymentProducts, setPaymentProducts] = useState<Product[]>([])

  // Shopify mode is driven ONLY by URL params (?shopify=true&shop=...). Every legitimate
  // entry into the Shopify flow carries them (ShopifyIntegration, store switch,
  // SubscriptionsTab all build the URL). Deliberately NO localStorage fallback: stored
  // params can outlive a cancelled/abandoned connect by up to 24h, and falling back would
  // hide the Stripe products and route plan clicks into Shopify checkout for a store the
  // merchant walked away from. Shopify is not the only product — never default to it.
  const location = useLocation()
  const history = useHistory()
  const queryParams = new URLSearchParams(location.search)
  const isShopifyFlow = queryParams.get('shopify') === 'true'
  const shopDomain = queryParams.get('shop')
  // Store-switch resubscribe: forwarded into the subscribe call so the post-purchase page
  // knows the org's existing data is the OLD store's and waits for the new store's sync.
  const switchedStore = queryParams.get('switched') === 'true'
  const [promoInput, setPromoInput] = useState('')
  const [appliedPromo, setAppliedPromo] = useState<IShopifyPromoCode | null>(null)
  // Sent with the subscribe call: the applied code, or whatever was typed without applying
  // (the backend checks it either way, so a typed code is never silently dropped).
  const promoCode = appliedPromo?.code || promoInput.trim()
  // Shown on the cards only when it applies to the selected billing.
  const cardPromo =
    appliedPromo && appliedPromo.billing_interval === (annualToggle ? 'ANNUAL' : 'EVERY_30_DAYS')
      ? appliedPromo
      : null

  // Returning leg of the subscription-confirm re-auth hop (#1658): the OAuth redirect
  // terminates here; consume the stashed (TTL-guarded) resume and route back to the
  // confirm page so the activation UX continues seamlessly with fresh credentials.
  useEffect(() => {
    if (!isShopifyFlow) return
    const resume = consumeConfirmResume()
    if (resume) history.replace(resume)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isLoadingOrganization && currentOrganization?.id) {
      getProducts()
    }
    // eslint-disable-next-line
  }, [isLoadingOrganization, currentOrganization?.id])

  useEffect(() => {
    const subscriptions: Product[] = []
    const payments: Product[] = []
    products.map(product => {
      if (product.mode === ProductMode.SUBSCRIPTION) subscriptions.push(product)
      else payments.push(product)
    })
    setSubscriptionProducts(subscriptions)
    setPaymentProducts(payments)
  }, [products])

  return (
    <div className="marketplace-wrapper">
      {isShopifyFlow && shopDomain && (
        <div className="shopify-banner" style={{
          padding: '16px',
          margin: '20px',
          marginBottom: '24px',
          backgroundColor: '#f0f7ff',
          border: '1px solid #96c7f7',
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0 }}>Welcome to Maya Insights!</h3>
          <p style={{ marginBottom: 0 }}>
            Select a plan for your Shopify store: <strong>{shopDomain}</strong>
          </p>
          <ShopifyPromoCode
            shopDomain={shopDomain}
            annual={annualToggle}
            setAnnual={setAnnualToggle}
            input={promoInput}
            setInput={setPromoInput}
            applied={appliedPromo}
            setApplied={setAppliedPromo}
          />
        </div>
      )}
      {/* shopify merchants only see subscriptions */}
      {!isShopifyFlow && (
        <MarketplaceCategory
          title="One-time payments"
          subheading="Select a product."
          products={paymentProducts}
          loadingProducts={loadingProducts}
          productError={productError}
        />
      )}
      <MarketplaceCategory
        title="Subscription plans"
        subheading="Select a Subscription plan."
        products={subscriptionProducts}
        loadingProducts={loadingProducts}
        productError={productError}
        // @ts-ignore
        annualToggle={annualToggle}
        setAnnualToggle={setAnnualToggle}
        isShopifyFlow={isShopifyFlow}
        shopDomain={shopDomain}
        switchedStore={switchedStore}
        promoCode={promoCode}
        promo={cardPromo}
      />
    </div>
  )
}

const MarketplaceCategory = ({
  title,
  subheading,
  products,
  loadingProducts,
  productError,
  annualToggle = null,
  setAnnualToggle = (toggle: boolean) => {},
  isShopifyFlow = false,
  shopDomain = null as string | null,
  switchedStore = false,
  promoCode = '',
  promo = null as IShopifyPromoCode | null,
}) => {
  return (
    <div className="products-wrapper">
      <div className="products-header">
        <div className="products-top-header">
          <h1>{title}</h1>
          <span className="subtitle-text">{subheading}</span>
          {productError.length > 0 ? (
            <p style={{ color: 'red' }}>{productError}</p>
          ) : (
            <></>
          )}
        </div>
        {annualToggle !== null && (
          <Switch
            left="Monthly Billing"
            right="Annual Billing"
            onChange={() => setAnnualToggle(!annualToggle)}
            checked={annualToggle}
          />
        )}
      </div>

      <div className="products-container">
        {products.length > 0 ? (
          products.map((product) => {
            // @ts-ignore
            return <ProductCard product={product} toggle={annualToggle} isShopifyFlow={isShopifyFlow} shopDomain={shopDomain} switchedStore={switchedStore} promoCode={promoCode} promo={promo} />
          })
        ) : loadingProducts ? (
          <ScreenLoading />
        ) : (
          products.length <= 0 && <p>No products found.</p>
        )}
      </div>
    </div>
  )
}

export default Products
